import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, Calendar, Trash2, Inbox, RefreshCw, StickyNote, Plus, X, Reply, Send } from 'lucide-react';
import { INQUIRIES_CHANGED } from '../components/NewInquiriesBadge';
import { InquiryAttachments } from '../components/InquiryAttachments';
import { toast } from 'sonner';
import axios from 'axios';

const STATUSES = [
  { value: 'new', label: 'Nová', color: 'bg-red-500' },
  { value: 'in_progress', label: 'Řeší se', color: 'bg-amber-500' },
  { value: 'done', label: 'Hotovo', color: 'bg-green-600' },
];

const formatDate = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const InquiryNotes = ({ inq, onAdd, onRemove }) => {
  const [text, setText] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (await onAdd(inq.id, text)) setText('');
  };
  return (
    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800" data-testid={`inquiry-notes-${inq.id}`}>
      <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
        <StickyNote size={14} /> Poznámky ({(inq.notes || []).length})
      </div>
      {(inq.notes || []).map((n) => (
        <div key={n.id} data-testid={`note-${n.id}`} className="group flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 rounded px-3 py-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(n.created_at)}</div>
            <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{n.text}</div>
          </div>
          <button onClick={() => onRemove(inq.id, n.id)} data-testid={`note-delete-${n.id}`} title="Smazat poznámku" className="text-gray-400 hover:text-red-600">
            <X size={14} />
          </button>
        </div>
      ))}
      <form onSubmit={submit} className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Např. volal jsem, posílám nabídku"
          data-testid={`note-input-${inq.id}`}
          className="flex-1 min-w-0 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
        />
        <button type="submit" data-testid={`note-add-${inq.id}`} className="flex items-center gap-1 bg-gray-900 dark:bg-white text-white dark:text-black px-3 py-2 rounded-lg text-sm font-medium">
          <Plus size={14} /> Přidat
        </button>
      </form>
    </div>
  );
};

const ReplyBox = ({ inq, onSend }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    if (await onSend(inq.id, text)) { setText(''); setOpen(false); }
    setSending(false);
  };
  return (
    <div className="mt-4" data-testid={`inquiry-replies-${inq.id}`}>
      {(inq.replies || []).map((r) => (
        <div key={r.id} data-testid={`reply-${r.id}`} className="bg-green-50 dark:bg-green-900/20 border-l-2 border-green-500 rounded px-3 py-2 mb-2">
          <div className="text-xs text-green-700 dark:text-green-400 font-semibold">Vaše odpověď · {formatDate(r.created_at)}</div>
          <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{r.text}</div>
        </div>
      ))}
      {open ? (
        <div className="space-y-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">Komu: {inq.email}</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            autoFocus
            placeholder={`Dobrý den ${inq.name}, ...`}
            data-testid={`reply-input-${inq.id}`}
            className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
          />
          <div className="flex gap-2">
            <button onClick={send} disabled={sending || !text.trim()} data-testid={`reply-send-${inq.id}`} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold">
              <Send size={14} /> {sending ? 'Odesílám...' : 'Odeslat odpověď'}
            </button>
            <button onClick={() => setOpen(false)} data-testid={`reply-cancel-${inq.id}`} className="px-4 py-2 rounded-lg text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">Zrušit</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid={`reply-open-${inq.id}`} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors duration-200">
          <Reply size={14} /> Odpovědět
        </button>
      )}
    </div>
  );
};

const InquiryCard = ({ inq, onStatus, onDelete, onAddNote, onRemoveNote, onReply }) => (
  <div data-testid={`inquiry-${inq.id}`} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 shadow-sm">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-testid={`inquiry-name-${inq.id}`}>{inq.name}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400 mt-1">
          <a href={`mailto:${inq.email}`} className="flex items-center gap-1 hover:text-green-600" data-testid={`inquiry-email-${inq.id}`}><Mail size={14} />{inq.email}</a>
          {inq.phone && <a href={`tel:${inq.phone}`} className="flex items-center gap-1 hover:text-green-600"><Phone size={14} />{inq.phone}</a>}
          <span className="flex items-center gap-1" data-testid={`inquiry-date-${inq.id}`}><Calendar size={14} />{formatDate(inq.created_at)}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 shrink-0">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => onStatus(inq.id, s.value)}
            data-testid={`inquiry-status-${s.value}-${inq.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors duration-200 ${inq.status === s.value ? `${s.color} text-white` : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          >
            {s.label}
          </button>
        ))}
        <button onClick={() => onDelete(inq.id)} data-testid={`inquiry-delete-${inq.id}`} title="Smazat" className="px-2 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 hover:bg-red-100">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm leading-relaxed" data-testid={`inquiry-message-${inq.id}`}>{inq.message}</p>
    <InquiryAttachments attachments={inq.attachments} />
    <ReplyBox inq={inq} onSend={onReply} />
    <InquiryNotes inq={inq} onAdd={onAddNote} onRemove={onRemoveNote} />
  </div>
);

const Inquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('new');

  const load = async () => {
    try {
      const { data } = await axios.get('/api/admin/inquiries');
      setItems(data);
    } catch (e) {
      toast.error('Chyba při načítání poptávek');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await axios.patch(`/api/admin/inquiries/${id}`, { status });
      window.dispatchEvent(new Event(INQUIRIES_CHANGED));
    } catch (e) {
      toast.error('Chyba při změně stavu');
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Opravdu smazat tuto poptávku?')) return;
    try {
      await axios.delete(`/api/admin/inquiries/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Poptávka smazána');
      window.dispatchEvent(new Event(INQUIRIES_CHANGED));
    } catch (e) {
      toast.error('Chyba při mazání');
    }
  };

  const addNote = async (id, text) => {
    try {
      const { data } = await axios.post(`/api/admin/inquiries/${id}/notes`, { text });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, notes: [...(i.notes || []), data] } : i)));
      return true;
    } catch (e) {
      toast.error('Chyba při ukládání poznámky');
      return false;
    }
  };

  const sendReply = async (id, text) => {
    try {
      const { data } = await axios.post(`/api/admin/inquiries/${id}/reply`, { text });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status: data.status, replies: [...(i.replies || []), data.reply] } : i)));
      toast.success('Odpověď odeslána');
      window.dispatchEvent(new Event(INQUIRIES_CHANGED));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Odpověď se nepodařilo odeslat');
      return false;
    }
  };

  const removeNote = async (id, noteId) => {
    if (!window.confirm('Smazat poznámku?')) return;
    try {
      await axios.delete(`/api/admin/inquiries/${id}/notes/${noteId}`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, notes: i.notes.filter((n) => n.id !== noteId) } : i)));
    } catch (e) {
      toast.error('Chyba při mazání poznámky');
    }
  };

  const tabs = [{ value: 'all', label: 'Vše' }, ...STATUSES];
  const shown = tab === 'all' ? items : items.filter((i) => i.status === tab);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black px-4 py-8 sm:px-6 lg:px-8" data-testid="inquiries-page">
      <div className="max-w-5xl mx-auto pb-20">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <Link to="/admin/dashboard" data-testid="inquiries-back" className="inline-flex items-center text-green-600 dark:text-green-400 hover:underline mb-2 text-sm">
              <ArrowLeft size={16} className="mr-1" /> Zpět na dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Poptávky</h1>
          </div>
          <button onClick={load} data-testid="inquiries-refresh" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg">
            <RefreshCw size={18} /> <span>Aktualizovat</span>
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="inquiries-tabs">
          {tabs.map((s) => (
            <button
              key={s.value}
              onClick={() => setTab(s.value)}
              data-testid={`inquiries-tab-${s.value}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${tab === s.value ? 'bg-green-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
            >
              {s.label} ({s.value === 'all' ? items.length : items.filter((i) => i.status === s.value).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-20">Načítám...</div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20 text-gray-500 dark:text-gray-400" data-testid="inquiries-empty">
            <Inbox size={56} className="mx-auto mb-3 opacity-30" />
            <p>Žádné poptávky v této složce</p>
          </div>
        ) : (
          <div className="space-y-4">
            {shown.map((inq) => <InquiryCard key={inq.id} inq={inq} onStatus={setStatus} onDelete={remove} onAddNote={addNote} onRemoveNote={removeNote} onReply={sendReply} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Inquiries;
